import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { messagingKeys } from '@/lib/queryKeys';
import { upsertRealtimeMessage, type MessageCache } from './messagePagination';
import type { ListMessagesParams, MessageChannel, MessageItem } from './types';

export interface RealtimeMessagePayload {
  tenantId?: string;
  messageId?: string;
  threadId?: string;
  customerId?: string | null;
  phoneNumberId?: string;
  channel?: MessageChannel;
  direction?: 'INBOUND' | 'OUTBOUND';
  body?: string;
  sourceIdentifier?: string;
  receivedAt?: string;
  sentAt?: string;
  createdAt?: string;
  senderUserId?: string;
  deliveryStatus?: string;
}

/**
 * Immediately project a committed message into every compatible cache for the
 * open thread. The server event is the source of truth; the normal
 * invalidation that follows still reconciles richer fields (for example MMS
 * attachment URLs) from the REST response.
 */
export function applyRealtimeMessage(
  queryClient: QueryClient,
  payload: RealtimeMessagePayload,
  activeThreadId: string | null | undefined,
): boolean {
  const message = toMessageItem(payload);
  if (!message || message.threadId !== activeThreadId) return false;

  const prefix = [...messagingKeys.thread(message.threadId), 'messages'] as const;
  const matchingQueries = queryClient.getQueryCache().findAll({ queryKey: prefix });
  let updated = false;

  for (const query of matchingQueries) {
    if (!matchesMessageFilters(query.queryKey, prefix.length, message)) continue;
    updated = true;
    queryClient.setQueryData<MessageCache>(query.queryKey, (cached) =>
      cached ? upsertRealtimeMessage(cached, message) : cached,
    );
  }
  return updated;
}

function toMessageItem(payload: RealtimeMessagePayload): MessageItem | null {
  const sentAt = payload.receivedAt ?? payload.sentAt ?? payload.createdAt;
  if (
    !payload.tenantId ||
    !payload.messageId ||
    !payload.threadId ||
    (payload.customerId !== null && !payload.customerId) ||
    !payload.channel ||
    (payload.direction !== 'INBOUND' && payload.direction !== 'OUTBOUND') ||
    typeof payload.body !== 'string' ||
    !payload.sourceIdentifier ||
    !sentAt
  ) {
    return null;
  }

  return {
    id: payload.messageId,
    tenantId: payload.tenantId,
    threadId: payload.threadId,
    customerId: payload.customerId,
    channel: payload.channel,
    direction: payload.direction,
    sourceIdentifier: payload.sourceIdentifier,
    fromNumberId: payload.phoneNumberId ?? null,
    senderUserId: payload.senderUserId ?? null,
    body: payload.body,
    attachments: null,
    externalMediaUrl: null,
    deliveryStatus: payload.deliveryStatus ?? null,
    errorCode: null,
    sentAt,
    deliveredAt: null,
    readAt: null,
    createdAt: payload.createdAt ?? sentAt,
  };
}

function matchesMessageFilters(
  queryKey: QueryKey,
  paramsIndex: number,
  message: MessageItem,
): boolean {
  const params = queryKey[paramsIndex] as ListMessagesParams | undefined;
  if (!params || typeof params !== 'object') return true;
  if (params.channel && params.channel !== message.channel) return false;
  if (params.direction && params.direction !== message.direction) return false;
  return true;
}
